/**
 * Storage Manager for iframe
 *
 * Manages key shares in ISOLATED localStorage (iframe's origin)
 * This is the CRITICAL security boundary - keys never leave this origin
 */

import type { KeyshareData } from './types'

/** A keyshare with its companion items, as stored (current or set aside) */
export interface StoredKeyshare {
  share: string
  ownerAddress: string
  sessionId: string | null
}

export class StorageManager {
  private readonly storage: Storage

  constructor() {
    if (typeof window === 'undefined' || !window.localStorage) {
      throw new Error('localStorage not available in iframe')
    }
    this.storage = window.localStorage

    console.log('[iframe][Storage] Initialized')
    console.log('[iframe][Storage] Origin:', window.location.origin)
  }

  /**
   * Save keyshare to IFRAME localStorage
   * CRITICAL: This is the secure isolated storage
   */
  saveKeyshare(userId: string, data: KeyshareData): void {
    try {
      const keyshareKey = `tss.${userId}.keyshare`

      // Store keyshare data
      this.storage.setItem(
        keyshareKey,
        JSON.stringify({
          keyshare_b64: data.keyshare_b64
        })
      )

      // Store owner address
      this.storage.setItem(`tss.${userId}.ownerAddress`, data.ownerAddress)

      // Store session ID
      this.storage.setItem(`tss.${userId}.sessionId`, data.sessionId)

      console.log(`[iframe][Storage] ✅ Saved keyshare for user: ${userId}`)
      console.log(`[iframe][Storage] Storage origin: ${window.location.origin}`)
    } catch (error) {
      console.error('[iframe][Storage] Failed to save keyshare:', error)
      throw new Error(`Failed to save keyshare: ${error instanceof Error ? error.message : 'Unknown error'}`)
    }
  }

  /**
   * Load keyshare from IFRAME localStorage
   */
  loadKeyshare(userId: string): KeyshareData | null {
    try {
      const keyshareKey = `tss.${userId}.keyshare`
      const keyshareData = this.storage.getItem(keyshareKey)

      if (!keyshareData) {
        console.log(`[iframe][Storage] No keyshare found for user: ${userId}`)
        return null
      }

      const parsed = JSON.parse(keyshareData)
      const ownerAddress = this.storage.getItem(`tss.${userId}.ownerAddress`)
      const sessionId = this.storage.getItem(`tss.${userId}.sessionId`)

      if (!ownerAddress || !sessionId) {
        console.error('[iframe][Storage] Missing owner address or session ID')
        return null
      }

      return {
        keyshare_b64: parsed.keyshare_b64,
        ownerAddress: ownerAddress as `0x${string}`,
        sessionId
      }
    } catch (error) {
      console.error('[iframe][Storage] Failed to load keyshare:', error)
      return null
    }
  }

  /**
   * Check if keyshare exists for user
   */
  hasKeyshare(userId: string): boolean {
    const keyshareKey = `tss.${userId}.keyshare`
    const ownerAddressKey = `tss.${userId}.ownerAddress`

    const hasKeyshare = this.storage.getItem(keyshareKey) !== null
    const hasAddress = this.storage.getItem(ownerAddressKey) !== null

    return hasKeyshare && hasAddress
  }

  /**
   * Get owner address for user
   */
  getOwnerAddress(userId: string): `0x${string}` | null {
    const address = this.storage.getItem(`tss.${userId}.ownerAddress`)
    return address ? (address as `0x${string}`) : null
  }

  /**
   * Get session ID for user
   */
  getSessionId(userId: string): string | null {
    return this.storage.getItem(`tss.${userId}.sessionId`)
  }

  /**
   * Remove keyshare for user
   */
  removeKeyshare(userId: string): void {
    try {
      this.storage.removeItem(`tss.${userId}.keyshare`)
      this.storage.removeItem(`tss.${userId}.ownerAddress`)
      this.storage.removeItem(`tss.${userId}.sessionId`)

      // Remove any DKG-related keys
      const allKeys = Object.keys(this.storage)
      const userKeys = allKeys.filter((key) => key.startsWith(`tss.${userId}.`))
      userKeys.forEach((key) => this.storage.removeItem(key))

      console.log(`[iframe][Storage] ✅ Removed keyshare for user: ${userId}`)
    } catch (error) {
      console.error('[iframe][Storage] Failed to remove keyshare:', error)
      throw new Error(`Failed to remove keyshare: ${error instanceof Error ? error.message : 'Unknown error'}`)
    }
  }

  /**
   * Make the stored keyshare the half of the server's one for the current project.
   *
   * A keyshare is only usable with the server keyshare it was generated with, and the server keeps
   * one per project or ecosystem while this storage keeps one per user. A keyshare that does not
   * match is moved aside, never deleted: it may be the right one for another project, and
   * restoreArchivedKeyshare brings it back when the server names its owner address again.
   *
   * `server.ownerAddress` undefined means an older TSS that does not report it: only the
   * unambiguous "server has none" case is acted on then.
   */
  reconcileWithServer(userId: string, server: { hasKeyshare: boolean; ownerAddress?: string | null }): void {
    const current = this.getOwnerAddress(userId)

    if (!server.hasKeyshare) {
      if (current) {
        console.warn(`[iframe][Storage] Server has no keyshare for this project, setting aside ${current}`)
        this.archiveKeyshare(userId)
      }
      return
    }

    const expected = server.ownerAddress
    if (!expected) return
    if (current && sameAddress(current, expected)) return

    if (this.restoreArchivedKeyshare(userId, expected)) return
    if (current) {
      console.warn(`[iframe][Storage] Keyshare ${current} is not the half of server keyshare ${expected}, setting it aside`)
      this.archiveKeyshare(userId)
    }
  }

  /**
   * Move the current keyshare aside under its owner address
   */
  archiveKeyshare(userId: string): void {
    // The stored items are moved as they are: a keyshare must never be dropped because one of
    // its companion items (the session id, say) is missing
    const share = this.storage.getItem(`tss.${userId}.keyshare`)
    const ownerAddress = this.storage.getItem(`tss.${userId}.ownerAddress`)
    if (!share || !ownerAddress) return
    const sessionId = this.storage.getItem(`tss.${userId}.sessionId`)

    const prefix = archivePrefix(userId, ownerAddress)
    this.storage.setItem(`${prefix}.share`, share)
    this.storage.setItem(`${prefix}.ownerAddress`, ownerAddress)
    if (sessionId !== null) this.storage.setItem(`${prefix}.sessionId`, sessionId)
    else this.storage.removeItem(`${prefix}.sessionId`)
    this.storage.removeItem(`tss.${userId}.keyshare`)
    this.storage.removeItem(`tss.${userId}.ownerAddress`)
    this.storage.removeItem(`tss.${userId}.sessionId`)
  }

  /**
   * Make the archived keyshare with this owner address current again; false when there is none
   */
  restoreArchivedKeyshare(userId: string, ownerAddress: string): boolean {
    const prefix = archivePrefix(userId, ownerAddress)
    const share = this.storage.getItem(`${prefix}.share`)
    if (!share) return false
    const storedAddress = this.storage.getItem(`${prefix}.ownerAddress`) ?? ownerAddress
    const sessionId = this.storage.getItem(`${prefix}.sessionId`)

    const current = this.getOwnerAddress(userId)
    if (current && !sameAddress(current, ownerAddress)) this.archiveKeyshare(userId)

    this.storage.setItem(`tss.${userId}.keyshare`, share)
    this.storage.setItem(`tss.${userId}.ownerAddress`, storedAddress)
    if (sessionId !== null) this.storage.setItem(`tss.${userId}.sessionId`, sessionId)
    else this.storage.removeItem(`tss.${userId}.sessionId`)
    this.storage.removeItem(`${prefix}.share`)
    this.storage.removeItem(`${prefix}.sessionId`)
    this.storage.removeItem(`${prefix}.ownerAddress`)
    console.log(`[iframe][Storage] ✅ Restored keyshare ${ownerAddress} for user: ${userId}`)
    return true
  }

  /**
   * Stored keyshare of wallet `ownerAddress`: the current one, or the one set aside under its owner address
   * (another tab may have made another wallet current). Without `ownerAddress`, the current one.
   * `share` is the item as stored.
   */
  getKeyshareOf(userId: string, ownerAddress?: string): StoredKeyshare | null {
    const current = this.getOwnerAddress(userId)
    if (!ownerAddress || (current && sameAddress(current, ownerAddress))) {
      const share = this.storage.getItem(`tss.${userId}.keyshare`)
      return share && current ? { share, ownerAddress: current, sessionId: this.getSessionId(userId) } : null
    }
    const prefix = archivePrefix(userId, ownerAddress)
    const share = this.storage.getItem(`${prefix}.share`)
    if (!share) return null
    return {
      share,
      ownerAddress: this.storage.getItem(`${prefix}.ownerAddress`) ?? ownerAddress,
      sessionId: this.storage.getItem(`${prefix}.sessionId`)
    }
  }

  /**
   * Set a keyshare aside under its owner address, unless this storage already holds one of that wallet
   * (current or set aside): an existing keyshare is never overwritten
   */
  keepAside(userId: string, data: StoredKeyshare): void {
    if (this.getKeyshareOf(userId, data.ownerAddress)) return
    const prefix = archivePrefix(userId, data.ownerAddress)
    this.storage.setItem(`${prefix}.share`, data.share)
    this.storage.setItem(`${prefix}.ownerAddress`, data.ownerAddress)
    if (data.sessionId !== null) this.storage.setItem(`${prefix}.sessionId`, data.sessionId)
    console.log(`[iframe][Storage] Keyshare of another wallet kept aside: ${data.ownerAddress}`)
  }

  /**
   * Make a restored keyshare the current one. A current keyshare of another wallet is set aside, never
   * overwritten (it may have no backup); a copy of the same wallet set aside before is replaced.
   */
  installKeyshare(userId: string, data: StoredKeyshare): void {
    const current = this.getOwnerAddress(userId)
    if (current && !sameAddress(current, data.ownerAddress)) this.archiveKeyshare(userId)
    const prefix = archivePrefix(userId, data.ownerAddress)
    this.storage.removeItem(`${prefix}.share`)
    this.storage.removeItem(`${prefix}.sessionId`)
    this.storage.removeItem(`${prefix}.ownerAddress`)
    this.storage.setItem(`tss.${userId}.keyshare`, data.share)
    if (data.sessionId !== null) this.storage.setItem(`tss.${userId}.sessionId`, data.sessionId)
    else this.storage.removeItem(`tss.${userId}.sessionId`)
    this.storage.setItem(`tss.${userId}.ownerAddress`, data.ownerAddress)
  }

  /**
   * List all users with keyshares (for debugging)
   */
  listUsers(): string[] {
    const allKeys = Object.keys(this.storage)
    const keyshareKeys = allKeys.filter((key) => key.endsWith('.keyshare'))

    return keyshareKeys
      .map((key) => {
        const match = key.match(/^tss\.(.+)\.keyshare$/)
        return match ? match[1] : null
      })
      .filter(Boolean) as string[]
  }

  /**
   * Get storage statistics (for debugging)
   */
  getStats(): { totalKeys: number; users: number; storageSize: number } {
    const allKeys = Object.keys(this.storage)
    const users = this.listUsers()

    let storageSize = 0
    allKeys.forEach((key) => {
      const value = this.storage.getItem(key)
      if (value) {
        storageSize += key.length + value.length
      }
    })

    return {
      totalKeys: allKeys.length,
      users: users.length,
      storageSize
    }
  }
}

function sameAddress(a: string, b: string): boolean {
  return a.toLowerCase() === b.toLowerCase()
}

// Not ending in `.keyshare`, so listUsers does not count archived keyshares as users
function archivePrefix(userId: string, ownerAddress: string): string {
  return `tss.${userId}.archive.${ownerAddress.toLowerCase()}`
}
